/* app/seo-pages.jsx - the Page Inventory for a WEBSITE, plus the one honest
   answer every CMS-requiring screen gives when the active site has no CMS
   behind it.

   THIS SCREEN IS PROPERTY SCOPED AND IT IS NO LONGER HIDDEN FROM A SITE WITH A
   CMS. docs/MODULE-SCOPE.md 4.5 retires the nav gate that hid it from anything
   but a crawl-only site, and section 7 settles where the screen lives: at the
   property, showing every page the website serves, with a section filter that
   narrows it. Hiding it on a CMS section would hide the only unfiltered view
   of the website from the customer who has a blog.

   PAGES IS THE CMS LENS AND PAGE INVENTORY IS THE SERVED LENS, and the two
   disagree on purpose: Pages shows what a connection holds, drafts included,
   and can write; this shows what the website serves, 200s and noindex
   included, and cannot. A page in the CMS the sitemap does not list, and a URL
   served that no CMS claims, are both real answers.

   EVERY ROW CARRIES BOTH HALVES OF ITS LABEL. "Found by this crawl" and
   "served by this section" are different facts (MODULE-SCOPE 7), the data
   keeps them apart, and a row that showed only one would put the conflation
   back into the UI after the data refused it. A page belonging to no section
   is listed under the piece that FOUND it and says so.

   Wired to the crawl contract, which answers at the property and takes an
   optional section filter:
     GET  /api/crawl/pages?site_id=N[&section_id=M]    the stored inventory
     POST /api/crawl/refresh?site_id=N                 per CRAWL CONNECTION, see refresh
     GET  /api/crawl/export?site_id=N[&section_id=M]&format=csv

   Lines and drawers, per the house rule: one line per page carrying the path,
   the title and the signals worth seeing at a glance, opening a drawer with
   the full record. Modelled on report-drawer.jsx (DetailLine): the same
   ds-line markup, the same design-system Drawer, the same Escape handling. It
   is not DetailLine itself because a page line needs a NEUTRAL state (most
   pages are fine, and DetailLine's three tones are all coloured) and carries
   its own two-part title plus signal chips. */
import { api, API_BASE } from '../lib/api.js';
(function () {
  const { useState, useEffect, useCallback } = React;
  function I(props) { return React.createElement(window.MlIcon, props); }
  const SeoStat = (p) => React.createElement(window.MlSeoDashboard.SeoStat, p);
  const fmt = (n) => Number(n || 0).toLocaleString();

  /** Is the active site the crawl-only kind? The single place that asks. */
  const isCrawlSite = (site) => !!(site && site.source === "crawl");

  /** The backend's refusal code for a WordPress-backed call on a crawl site. */
  const isCrawlOnlyError = (e) => !!(e && e.data && e.data.code === "CRAWL_ONLY_SITE");

  // Navigation out of here goes through the hash, which is the app's own
  // location convention (see parseLocationHash in main.jsx) and is listened to
  // for hashchange. That matters for the notice below: it is rendered on behalf
  // of screens that were never handed a navigate prop, so asking for one would
  // mean threading it through half the app to deliver three links.
  const goTo = (screen) => { window.location.hash = "#" + screen; };

  const siteIdOf = (site) => (site && (site.site_id != null ? site.site_id : site.id));
  const hostOf = (site) => String((site && (site.url || site.urlFull)) || "").replace(/^https?:\/\//, "").replace(/\/+$/, "");
  const nameOf = (s) => (s && s.name) || "this site";
  // "A", "A and B", "A, B and C": a list of sections read as a sentence,
  // because a figure summed or partitioned over sections is only checkable
  // when the sections are named.
  function listNames(arr) {
    const n = (arr || []).map(x => (typeof x === "string" ? x : nameOf(x)));
    if (n.length <= 1) return n[0] || "";
    return n.slice(0, -1).join(", ") + " and " + n[n.length - 1];
  }

  /**
   * The SECTION half of a row's label, as the short words that go on a line.
   *
   * Three outcomes and they are three different facts, which is the whole
   * reason the server sends the pair rather than a nullable name:
   *   a section serves it            the section's name
   *   belongs_to_no_section          a settled answer: no piece of this
   *                                  website declares an address reaching it,
   *                                  which is what a hand coded page IS
   *   section_not_derived_yet        not an answer at all: nothing has scoped
   *                                  the row, so its empty section is unknown
   */
  function servedLabel(label) {
    if (!label) return "Section not recorded";
    if (label.section_name) return "Served by " + label.section_name;
    if (label.belongs_to_no_section) return "No section serves it";
    return "Section not derived yet";
  }
  const foundLabel = (label) => "Found by " + ((label && label.found_by_name) || "nothing recorded");

  function when(iso) {
    if (!iso) return "never";
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return String(iso);
    const s = Math.max(1, Math.floor((Date.now() - t) / 1000));
    if (s < 60) return "just now";
    const m = Math.floor(s / 60); if (m < 60) return m + "m ago";
    const h = Math.floor(m / 60); if (h < 24) return h + "h ago";
    const d = Math.floor(h / 24); if (d < 30) return d + "d ago";
    return new Date(iso).toLocaleDateString();
  }
  function stamp(iso) {
    if (!iso) return "-";
    const t = new Date(iso).getTime();
    return Number.isFinite(t) ? new Date(iso).toLocaleString() : String(iso);
  }

  // ---- the honest answer on a screen that needs a CMS ----------------------
  /**
   * One message, used by every screen that reads or writes a CMS, shown in
   * place of that screen when the active site has none.
   *
   * It is deliberately not an error and not a spinner: nothing has gone wrong,
   * this site was added as a site MarketLens measures rather than one it
   * publishes to. So it names what is missing, says why it is missing, and
   * points at the three things that DO work here.
   */
  function CrawlOnlyNotice({ site, title }) {
    const name = (site && site.name) || "this site";
    return (
      <div>
        <div className="ml-pagehead">
          <div className="ml-pagehead__titles">
            <h1 className="ml-pagehead__title">{title || "Not available here"}</h1>
            <p className="ml-pagehead__sub">No CMS is connected to {name}.</p>
          </div>
        </div>
        <div className="bi-empty-state">
          <span className="bi-empty-state__icon"><I name="eye" size={30} /></span>
          <div className="bi-empty-state__title">This site has no CMS to read or write</div>
          <p className="bi-empty-state__desc">
            MarketLens reaches {name} by crawling its public pages, so there is nothing to sign in to and nothing it can write back.
            Publishing, editing and taxonomy work all stay wherever this site is built.
            What MarketLens does here is measure: the pages it found, the issues in them, and how they perform in search.
            Every change it recommends is one you make yourself.
          </p>
          <div className="bi-empty-state__actions">
            <button type="button" className="bi-btn bi-btn--primary" onClick={() => goTo("page-inventory")}><I name="list" size={16} />Page Inventory</button>
            <button type="button" className="bi-btn" onClick={() => goTo("site-audit")}><I name="shield-check" size={16} />Site Audit</button>
            <button type="button" className="bi-btn" onClick={() => goTo("performance")}><I name="trending-up" size={16} />Performance</button>
          </div>
        </div>
      </div>
    );
  }

  // ---- signals on a page ---------------------------------------------------
  const TITLE_MAX = 60;

  /**
   * The things worth flagging on a page, strongest first.
   *
   * word_count is NOT in here, and that is a decision rather than an omission:
   * the sites this screen exists for are client-rendered SPAs. Their served
   * HTML carries a prerendered <head> and an essentially empty <body>, so a
   * page that is completely healthy reports a word count near zero. Flagging
   * that would put an amber badge on every row of a perfectly good site, which
   * is a false alarm seventy times over. The count is reported separately, as
   * an observation with no severity, by wordChip below. Please leave it there.
   */
  function problemsOf(p) {
    const out = [];
    const status = Number(p.status || 0);
    if (status && status !== 200) {
      out.push({ tone: "danger", label: "HTTP " + status, why: "The crawler did not get a 200 for this URL. A page in the sitemap that does not answer is worth fixing first." });
    }
    if (p.noindex) {
      out.push({ tone: "warning", label: "noindex", why: "This page tells search engines not to index it. Intentional on a thank-you page, a problem on anything you want found." });
    }
    const title = String(p.title || "").trim();
    if (!title) out.push({ tone: "warning", label: "No title", why: "The page has no title tag, so search results will show whatever Google picks instead." });
    else if (title.length > TITLE_MAX) out.push({ tone: "warning", label: "Title " + title.length, why: "Longer than " + TITLE_MAX + " characters, so search results are likely to cut it short." });
    if (!String(p.description || "").trim()) {
      out.push({ tone: "warning", label: "No description", why: "No meta description, so the snippet under your result is written by Google rather than by you." });
    }
    return out;
  }

  /**
   * The word count, as an observation with no severity attached. See the note
   * in problemsOf: on a client-rendered site a near-zero count is normal, so
   * this chip must stay neutral no matter how low the number is.
   */
  function wordChip(p) {
    const n = Number(p.word_count || 0);
    return {
      label: fmt(n) + (n === 1 ? " word" : " words"),
      why: "Words counted in the HTML this URL served. A client-rendered site serves an almost empty body and fills it in the browser, so a low count here is an observation about the HTML and not a problem with the page.",
    };
  }

  // ---- the full record, in the drawer --------------------------------------
  // A plain function returning JSX rather than a component: defining a
  // component inside a render gives it a new type on every pass, React remounts
  // it, and anything focusable inside loses focus on every keystroke.
  function recordRow(label, value) {
    return (
      <div className="crawl-rec__row" key={label}>
        <span className="crawl-rec__k">{label}</span>
        <span className="crawl-rec__v">{value}</span>
      </div>
    );
  }
  const or = (v, fallback) => {
    const s = v == null ? "" : String(v).trim();
    return s ? s : <span className="crawl-rec__none">{fallback}</span>;
  };
  function pageRecord(page) {
    const title = String(page.title || "").trim();
    const desc = String(page.description || "").trim();
    const flags = problemsOf(page);
    const words = wordChip(page);
    return (
      <div className="crawl-rec">
        {flags.length > 0 && (
          <div className="crawl-rec__flags">
            {flags.map((f, i) => <span key={i} className={"hds-badge hds-badge--" + f.tone}>{f.label}</span>)}
          </div>
        )}
        {flags.length > 0 && (
          <ul className="crawl-rec__why">
            {flags.map((f, i) => <li key={i}>{f.why}</li>)}
          </ul>
        )}
        {recordRow("URL", page.url
          ? <a href={page.url} target="_blank" rel="noopener noreferrer">{page.url} <I name="external-link" size={12} /></a>
          : or("", "not recorded"))}
        {recordRow("Path", or(page.path, "not recorded"))}
        {recordRow("HTTP status", page.status != null ? String(page.status) : or("", "not recorded"))}
        {recordRow("Title", title ? <>{title} <span className="crawl-rec__len">{title.length} characters</span></> : or("", "none"))}
        {recordRow("Meta description", desc ? <>{desc} <span className="crawl-rec__len">{desc.length} characters</span></> : or("", "none"))}
        {recordRow("Canonical", or(page.canonical, "none declared"))}
        {recordRow("H1", or(page.h1, "none found"))}
        {recordRow("Language", or(page.lang, "not declared"))}
        {recordRow("Words in the served HTML", <>{words.label} <span className="crawl-rec__note">{words.why}</span></>)}
        {recordRow("Indexable", page.noindex ? "No: this page asks not to be indexed" : "Yes: nothing on the page refuses indexing")}
        {recordRow("Sitemap lastmod", page.lastmod ? stamp(page.lastmod) : or("", "not given in the sitemap"))}
        {recordRow("Last crawled", page.crawled_at ? stamp(page.crawled_at) : or("", "not recorded"))}
        {/* The two halves, spelled out in full here because the line above has
            room only for the short words. `label.text` is the server's own
            sentence for this row and says which of the two empty sections this
            is: a settled "no section serves it" or an unscoped row whose empty
            section is not an answer yet. */}
        {recordRow("Found by", or(page.label && page.label.found_by_name, "nothing recorded"))}
        {recordRow("Section that serves it", page.label && page.label.section_name
          ? page.label.section_name
          : or("", page.label && page.label.belongs_to_no_section ? "no section of this website" : "not derived yet"))}
        {page.label && page.label.text && recordRow("What that means", page.label.text)}
      </div>
    );
  }

  // ---- one page, one line, one drawer -------------------------------------
  function PageLine({ page }) {
    const [open, setOpen] = useState(false);
    const DS = window.HuddleDesignSystem_775113;
    const Drawer = DS && DS.Drawer;

    // Escape closes the drawer. Bound only while it is open, so a line at rest
    // costs nothing and swallows nobody else's Escape. Same as DetailLine.
    useEffect(() => {
      if (!open) return;
      const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    const flags = problemsOf(page);
    const words = wordChip(page);
    // A clean page gets NO tone, which leaves ds-line's neutral left border.
    // Defaulting to "todo" like DetailLine does would paint a healthy
    // seventy-page site entirely amber.
    const tone = flags.some(f => f.tone === "danger") ? "problem" : flags.length ? "todo" : "";
    const path = page.path || page.url || "(no path)";
    const title = String(page.title || "").trim();

    return (
      <>
        <div className={"ds-line crawl-line" + (tone ? " ds-line--" + tone : "")}
             role="button" tabIndex={0} aria-expanded={open}
             title="Open everything the crawler read on this page"
             onClick={() => setOpen(true)}
             /* Keyboard path for the line itself only: Enter on the link
                inside is that link's business. */
             onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setOpen(true); } }}>
          <span className="ds-card__icon"><I name={tone === "problem" ? "alert-triangle" : "file-text"} size={15} /></span>
          <span className="ds-line__title crawl-line__main">
            <span className="crawl-line__path">{path}</span>
            <span className="crawl-line__name">{title || "No title"}</span>
          </span>
          <span className="crawl-line__flags">
            {flags.map((f, i) => <span key={i} className={"hds-badge hds-badge--" + f.tone} title={f.why}>{f.label}</span>)}
            {/* BOTH HALVES OF THE LABEL, on every row and at both positions.
                They are two different facts: the finder is the crawl that
                observed this URL, the section is the piece of the website that
                serves it, and a page can be found by one and served by none.
                Showing one of them would be the conflation the data refused.
                Neutral badges, because neither is a problem with the page. */}
            {page.label && (
              <>
                <span className="hds-badge hds-badge--neutral" title={page.label.text}>{servedLabel(page.label)}</span>
                <span className="hds-badge hds-badge--neutral" title={page.label.text}>{foundLabel(page.label)}</span>
              </>
            )}
            {/* Neutral on purpose: see problemsOf. */}
            <span className="hds-badge hds-badge--neutral crawl-line__obs" title={words.why}>{words.label}</span>
          </span>
          {/* The one action that belongs on the line, because the live page is
              the thing being judged. It stops the click: opening the site must
              not also open the drawer behind it. */}
          {page.url && (
            <a className="crawl-line__visit" href={page.url} target="_blank" rel="noopener noreferrer"
               title="Open this page in a new tab" aria-label="Open this page in a new tab"
               onClick={(e) => e.stopPropagation()}><I name="external-link" size={14} /></a>
          )}
          <span className="ds-card__icon ml-rdrawer-chev"><I name="chevron-right" size={15} /></span>
        </div>
        {open && (Drawer
          ? <Drawer title={path} subtitle={title || "No title"} onClose={() => setOpen(false)}>{pageRecord(page)}</Drawer>
          /* The design-system bundle failing to load is index.html's problem,
             but a click that does nothing must not be this line's answer to
             it: the record renders inline instead. */
          : <div className="ds-strip">{pageRecord(page)}</div>)}
      </>
    );
  }

  // ---- the CSV, from an authenticated API ---------------------------------
  /**
   * The filename the server asked for, falling back to one built from the site.
   * Content-Disposition is readable here because the API is same-origin.
   */
  function filenameFrom(res, site) {
    const cd = res.headers.get("Content-Disposition") || "";
    const star = cd.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    const plain = cd.match(/filename\s*=\s*"?([^";]+)"?/i);
    const raw = (star && star[1]) || (plain && plain[1]);
    if (raw) { try { return decodeURIComponent(raw.trim()); } catch { return raw.trim(); } }
    const host = (hostOf(site) || "site").replace(/[^a-z0-9.-]/gi, "-");
    return "marketlens-pages-" + host + ".csv";
  }

  // ---- the screen ---------------------------------------------------------
  function PageInventory({ site, sectionId, sections }) {
    // THE ANCHOR, unchanged: site_id is what enforceSiteScope authorises, and
    // the property is derived from it on the server rather than passed in. The
    // section is an extra filter on top and never a replacement for it.
    const siteId = siteIdOf(site);
    const atAll = sectionId == null;
    const list = (Array.isArray(sections) && sections.length) ? sections : (site ? [site] : []);
    const section = atAll ? null : (list.find(x => siteIdOf(x) === sectionId) || site);
    // THE CRAWL CONNECTIONS, which are what a refresh can be pressed against.
    // POST /crawl/refresh takes a site_id and refuses anything whose source is
    // not `crawl`, because it fetches a sitemap and stores what it read. It
    // takes NO section_id, deliberately: a crawl is a connection and a section
    // is a display question, so there is nothing for a filter to narrow in a
    // write that re-reads one sitemap. At All every crawl of the website is a
    // target; filtered, only the named section if it is itself a crawl.
    const crawlTargets = (atAll ? list : (section ? [section] : [])).filter(x => x && x.source === "crawl");
    const [inv, setInv] = useState({ loading: true, err: null, pages: [], crawledAt: null, count: 0, state: null, breakdown: [], sectionsWhy: null, noSection: null, setup: null, scope: null });
    const [refreshing, setRefreshing] = useState(null);   // null | { done, total, name }
    const [report, setReport] = useState(null);           // { ok, runs: [...], crawled, stored, removed }
    const [exporting, setExporting] = useState(false);
    const [exportErr, setExportErr] = useState(null);
    const [q, setQ] = useState("");
    const [view, setView] = useState("all");         // all | flagged

    // The section list arrives as a fresh array on every render, because the
    // shell derives it by filtering the account's sites. `load` therefore
    // depends on the two ids and on nothing derived from that array: a read
    // that re-ran whenever its props were rebuilt would call the API in a loop
    // for as long as the screen was open. The refresh targets ARE derived from
    // it, so they are reached through a ref that is kept current rather than
    // captured in a dependency.
    const targetsRef = React.useRef(crawlTargets); targetsRef.current = crawlTargets;

    const load = useCallback(async () => {
      if (siteId == null) { setInv({ loading: false, err: null, pages: [], crawledAt: null, count: 0, state: null, breakdown: [], sectionsWhy: null, noSection: null, setup: null, scope: null }); return; }
      setInv(s => ({ ...s, loading: true, err: null }));
      try {
        const r = await api(`/crawl/pages?site_id=${siteId}${atAll ? "" : `&section_id=${sectionId}`}`);
        setInv({
          loading: false, err: null,
          pages: Array.isArray(r && r.pages) ? r.pages : [],
          crawledAt: (r && r.crawled_at) || null,
          // Live state, from the rows this answer holds, which are not capped.
          count: Number((r && r.count) || 0),
          // Which KIND of empty this is, when it is empty: unread, ungrouped,
          // or read and genuinely empty, which are three different answers.
          state: (r && r.state) || null,
          // The partition, per piece of the website, which adds up to `count`.
          breakdown: Array.isArray(r && r.sections) ? r.sections : [],
          sectionsWhy: (r && r.sections_why) || null,
          noSection: (r && r.no_section) || null,
          setup: (r && r.setup) || null,
          scope: (r && r.scope) || null,
        });
      } catch (e) {
        setInv({ loading: false, err: e, pages: [], crawledAt: null, count: 0, state: null, breakdown: [], sectionsWhy: null, noSection: null, setup: null, scope: null });
      }
    }, [siteId, atAll, sectionId]);
    useEffect(() => { load(); }, [load]);

    // A REFRESH RUNS ONE CRAWL CONNECTION, so at All it is run once per crawl
    // of the website, in turn, naming the one in flight. Pressing it against
    // the anchor alone would re-read one piece of the website and then reload a
    // list covering all of it, which looks like a refresh and is not one.
    const refresh = async () => {
      const targets = targetsRef.current;
      if (refreshing || !targets.length) return;
      const runs = [];
      for (let i = 0; i < targets.length; i++) {
        const t = targets[i];
        setRefreshing({ done: i, total: targets.length, name: nameOf(t) });
        setReport(null);
        try {
          const r = await api(`/crawl/refresh?site_id=${siteIdOf(t)}`, { method: "POST", body: "{}" });
          // `note` is the server's own sentence for an outcome the numbers
          // alone would misreport: a crawl that read nothing leaves the
          // previous inventory in place rather than emptying it, and "0
          // removed" on its own would read as "your site has no pages".
          runs.push({ name: nameOf(t), ok: true, crawled: r && r.crawled, stored: r && r.stored, removed: r && r.removed, sitemapUrl: r && r.sitemapUrl, note: (r && r.note) || null });
        } catch (e) {
          runs.push({ name: nameOf(t), ok: false, message: (e && e.message) || "The crawl could not be run." });
        }
      }
      setRefreshing(null);
      const sum = (k) => runs.reduce((n, x) => n + Number((x.ok && x[k]) || 0), 0);
      setReport({ ok: runs.every(x => x.ok), runs, crawled: sum("crawled"), stored: sum("stored"), removed: sum("removed") });
      await load();
    };

    /**
     * The export is a file download from an authenticated API, so it cannot be
     * a plain <a href>: that is a top-level navigation, it leaves the app's own
     * fetch contract behind, and a session failure arrives as an error page
     * saved into the user's downloads folder. There was no precedent for an
     * authenticated download in this app, so this follows api()'s contract
     * instead (same-origin /api, credentials included), then turns the body
     * into a Blob and clicks a temporary object URL. A non-OK response is
     * read as JSON, because that is what the API answers errors with: showing
     * the server's own sentence beats saving the failure as a .csv.
     */
    const exportCsv = async () => {
      if (siteId == null || exporting) return;
      setExporting(true); setExportErr(null);
      let href = null;
      try {
        // The SAME section filter as the list, which the route insists on for
        // a reason worth repeating here: an export covering a different set of
        // rows from the list it was pressed on is a quiet second answer to
        // "which pages are these", and a developer would apply it to the wrong
        // part of the website. The server names the file after the filter too,
        // so two downloads in one folder cannot collide.
        const res = await fetch(`${API_BASE}/crawl/export?site_id=${siteId}${atAll ? "" : `&section_id=${sectionId}`}&format=csv`, {
          credentials: "include",
          headers: { Accept: "text/csv" },
        });
        if (!res.ok) {
          let msg = `The export failed (${res.status}).`;
          try { const j = await res.json(); if (j && j.error) msg = j.error; } catch { /* not JSON */ }
          throw new Error(msg);
        }
        const blob = await res.blob();
        href = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = href;
        a.download = filenameFrom(res, site);
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (e) {
        setExportErr((e && e.message) || "The export could not be downloaded.");
      } finally {
        // Revoked on a delay rather than immediately: some browsers finish
        // reading the blob after the click returns, and pulling the URL out
        // from under them cancels the download.
        if (href) setTimeout(() => URL.revokeObjectURL(href), 10000);
        setExporting(false);
      }
    };

    const pages = inv.pages;
    const term = q.trim().toLowerCase();
    const searched = term
      // The section name is searchable too, because at All the question "which
      // of these are the blog's" is now one a customer can have.
      ? pages.filter(p => (String(p.path || "") + " " + String(p.url || "") + " " + String(p.title || "")
          + " " + String((p.label && p.label.section_name) || "") + " " + String((p.label && p.label.found_by_name) || "")
        ).toLowerCase().includes(term))
      : pages;
    const rows = view === "flagged" ? searched.filter(p => problemsOf(p).length > 0) : searched;
    const flagged = pages.filter(p => problemsOf(p).length > 0).length;
    const noDesc = pages.filter(p => !String(p.description || "").trim()).length;
    const broken = pages.filter(p => { const s = Number(p.status || 0); return s && s !== 200; }).length;

    const ReportStrip = window.MlReportDrawer && window.MlReportDrawer.ReportStrip;
    const DS = window.HuddleDesignSystem_775113;

    const Header = (
      <div className="bi-header">
        <div className="bi-header__titles">
          <h1 className="bi-title"><span className="bi-title__icon"><I name="list" size={20} /></span>Page Inventory</h1>
          <div className="bi-subtitle">
            {atAll ? "Every page this website serves" : `Pages served by ${nameOf(section)}`}
            {atAll ? ((site && (site.property_name || site.url)) ? " · " + (site.property_name || site.url) : "") : ""}
            {inv.crawledAt ? " · last crawled " + when(inv.crawledAt) : ""}
          </div>
        </div>
        <div className="bi-header-actions">
          <button className="bi-btn" disabled={exporting || !pages.length} onClick={exportCsv}
                  title={pages.length
                    ? (atAll ? "Download every page of this website as a CSV" : `Download ${nameOf(section)}'s pages as a CSV`)
                    : "There is nothing to export yet"}>
            {exporting
              ? <><span className="ml-spinner ml-spinner--sm" style={{ width: 14, height: 14 }} />Preparing…</>
              : <><I name="download" size={16} />Export CSV</>}
          </button>
          {/* No crawl connection in this position means nothing to press, and
              the button says which rather than failing: the route refuses any
              site whose source is not `crawl`, so offering it on a CMS section
              would be an invitation to an error. */}
          <button className="bi-btn bi-btn--primary" disabled={!!refreshing || !crawlTargets.length} onClick={refresh}
                  title={crawlTargets.length
                    ? (crawlTargets.length > 1
                      ? `A refresh re-reads one crawl, so this runs once per crawl of the website, in turn: ${listNames(crawlTargets)}.`
                      : `Re-read ${nameOf(crawlTargets[0])} and store what it serves now.`)
                    : (atAll
                      ? "No crawl has been added to this website, so there is no sitemap to re-read."
                      : `${nameOf(section)} is read through its CMS, not by crawling, so there is nothing here to refresh.`)}>
            {refreshing
              ? <><span className="ml-spinner ml-spinner--sm ml-spinner--on-primary" style={{ width: 14, height: 14 }} />{`Crawling ${refreshing.name} (${refreshing.done + 1} of ${refreshing.total})…`}</>
              : <><I name="refresh" size={16} />{crawlTargets.length > 1 ? "Refresh every crawl" : "Refresh"}</>}
          </button>
        </div>
      </div>
    );

    // THE CMS GATE IS GONE, and MODULE-SCOPE 4.5 is the argument. This screen
    // used to answer a site with a CMS by refusing: "this site has a CMS, so
    // its pages are not crawled", and pointing at Pages. That was right while
    // the screen was one connection's crawl and is backwards now that it is the
    // website's served lens, read at the property. A section with a CMS has
    // served pages like any other part of the website, the crawl at the
    // property root found them, and refusing to list them would hide the only
    // unfiltered view of the website from the customer who has a blog.
    //
    // What replaces the gate is not a different refusal but the ordinary empty
    // states below, driven by `state` from the server: unread, not grouped
    // under a property yet, or read and genuinely empty. A section with no
    // pages is READ AND EMPTY, which is an answer about the website, and it
    // keeps every tool on this screen.

    // The refresh outcome, folded to one line that states it with its numbers
    // and opens the detail: the house idiom for a finished run (ReportStrip,
    // see report-drawer.jsx). A crawl that read nothing is NOT reported as a
    // calm success: the numbers would all be zero and the stored inventory was
    // deliberately left alone, which is a thing to look at, not to shrug at.
    //
    // One press can be several runs now, so the strip totals them and the
    // drawer keeps each crawl on its own: three crawls are three sitemaps and
    // three outcomes, and a single merged figure would hide the one that failed.
    const runs = (report && report.runs) || [];
    const failedRuns = runs.filter(x => !x.ok);
    const readNothing = report && report.ok && !Number(report.crawled);
    const Report = (report && ReportStrip)
      ? React.createElement(ReportStrip, {
          title: !report.ok
            ? (failedRuns.length === runs.length
              ? (runs.length === 1 ? "The crawl did not finish" : "No crawl finished")
              : `${failedRuns.length} of ${runs.length} crawls did not finish: ${listNames(failedRuns.map(x => x.name))}`)
            : readNothing
              ? (runs.length === 1
                ? "The crawl read no pages, so the inventory was left as it was"
                : "No crawl read any pages, so the inventory was left as it was")
              : `Crawled ${fmt(report.crawled)} page${Number(report.crawled) === 1 ? "" : "s"}${runs.length > 1 ? ` over ${runs.length} crawls` : ""}: ${fmt(report.stored)} stored, ${fmt(report.removed)} removed`,
          tone: (!report.ok || readNothing) ? "problem" : "opportunity",
          onDismiss: () => setReport(null),
        }, (
          <div>
            {runs.map((r, i) => (
              <div className="crawl-rec" key={r.name + ":" + i} style={i ? { marginTop: 14 } : undefined}>
                {runs.length > 1 && recordRow("Crawl", r.name)}
                {r.ok ? (
                  <>
                    {recordRow("Pages crawled", fmt(r.crawled))}
                    {recordRow("Stored in the inventory", fmt(r.stored))}
                    {recordRow("Removed", fmt(r.removed) + (Number(r.removed) ? ": these URLs are no longer in the sitemap" : ""))}
                    {recordRow("Sitemap read", r.sitemapUrl
                      ? <a href={r.sitemapUrl} target="_blank" rel="noopener noreferrer">{r.sitemapUrl} <I name="external-link" size={12} /></a>
                      : or("", "no sitemap was found"))}
                    {r.note && recordRow("What that means", r.note)}
                  </>
                ) : recordRow("Did not finish", r.message)}
              </div>
            ))}
            <p className="crawl-rec__foot">
              Everything above was read from the live website. A crawl only reads: nothing on the site was changed by it.
            </p>
          </div>
        ))
      : null;

    return (
      <div className="bi-page">{Header}
        <p className="seo-intro">
          {atAll
            ? <>Every page this website serves, read from its sitemaps and then from each page's served HTML. This is the <b>served lens</b>: what a visitor and a search engine really get. Pages is the CMS lens and shows what a connection holds, drafts included, so the two are allowed to disagree and the disagreement is a finding.</>
            : <>The pages {nameOf(section)} serves, read from the website's sitemaps and then from each page's served HTML. A page found by a crawl that no section serves is listed under the piece that found it, and says so.</>}
            {" "}Every row names both: the crawl that found it, and the section that serves it. A line opens everything read on that page.
        </p>

        {Report}

        {/* The server's own sentence, whichever refusal it was. It names the
            case precisely (a section that is not part of this website, a
            connection with no property behind it), and a message of our own
            invented over the top of it would be a second, vaguer answer. */}
        {inv.err && DS && React.createElement(DS.Banner, { tone: "danger", title: "Couldn't read the inventory" },
          (inv.err && inv.err.message) || "Request failed.")}
        {exportErr && DS && React.createElement(DS.Banner, { tone: "danger", title: "Export failed" }, exportErr)}

        <div className="seo-stat-row">
          <SeoStat icon="list" label="Pages found" value={fmt(inv.count || pages.length)} loading={inv.loading} />
          <SeoStat icon="alert-triangle" label="Pages with signals" value={fmt(flagged)} loading={inv.loading} />
          <SeoStat icon="file-text" label="No description" value={fmt(noDesc)} loading={inv.loading} />
          <SeoStat icon="x-circle" label="Not answering 200" value={fmt(broken)} loading={inv.loading} />
        </div>

        {/* THE PARTITION, which adds up to the count above it, and only at All:
            filtered, the same figures would describe the rows the filter
            selected rather than the website, which is a different statement
            wearing the same clothes. Two columns per section and they are kept
            apart on purpose: what the section SERVES is the stored attribution,
            what it FOUND AND NO SECTION SERVES is the display rule. Adding them
            into one figure here would be the conflation the per row label
            refuses. */}
        {atAll && !inv.loading && inv.breakdown.length > 0 && (
          <div className="seo-scope">
            {inv.breakdown.map(b => (
              <span className="csite-badge" key={b.section_id}
                    title={`${b.name} serves ${fmt(b.serves)} of these pages`
                      + (b.found_and_unclaimed ? ` and found ${fmt(b.found_and_unclaimed)} more that no section of this website serves` : "")
                      + (b.is_crawl ? ". It is read by crawling and has no CMS behind it." : ".")}>
                {b.name}: <b>{fmt(b.pages)}</b>
                {b.found_and_unclaimed ? ` (${fmt(b.serves)} served, ${fmt(b.found_and_unclaimed)} unclaimed)` : ""}
              </span>
            ))}
            {inv.sectionsWhy && <span className="seo-scope__label">{inv.sectionsWhy}</span>}
          </div>
        )}

        {/* The two counts that may be shown for an empty section, never a bare
            count of nulls: a page judged and belonging to no section is a
            legitimate state and is what the hand coded part of a website IS,
            while a row nothing has scoped yet has an empty section that is not
            an answer. One figure over both would report a finished job as
            permanently short. */}
        {!inv.loading && inv.noSection && (inv.noSection.settled_null > 0 || inv.noSection.outstanding > 0) && (
          <div className="seo-scope">
            {inv.noSection.settled_null > 0 && (
              <span className="seo-scope__label" title={inv.noSection.why}>
                {fmt(inv.noSection.settled_null)} of these pages belong to no section of this website, which is an answer and not work left to do.
              </span>
            )}
            {inv.noSection.outstanding > 0 && (
              <span className="seo-scope__label" title={inv.noSection.why}>
                {fmt(inv.noSection.outstanding)} rows have not been scoped yet, so their empty section is not an answer.
              </span>
            )}
          </div>
        )}

        {/* A MISSING DECLARATION READS AS A WEBSITE WITH NO SECTIONS unless it
            is said out loud, which is what these two sentences are for. If the
            property has not declared its public address there are no two
            addresses to compare, so every page comes back belonging to no
            section: a gap in the setup rather than a fact about the website. */}
        {!inv.loading && inv.setup && (inv.setup.public_base_why || inv.setup.nothing_can_claim_why) && (
          <div className="seo-scope">
            <span className="seo-scope__label">{inv.setup.public_base_why || inv.setup.nothing_can_claim_why}</span>
          </div>
        )}

        {inv.loading ? (
          <div className="bi-subempty">Reading the inventory…</div>
        ) : pages.length === 0 && !inv.err ? (
          /* AN EMPTY ANSWER IS SEVERAL DIFFERENT ANSWERS, and `state` from the
             server is what tells them apart:
               nothing has crawled this website yet       press Refresh
               the connection is not grouped under a property yet
               READ AND EMPTY: the crawling has read the website and none of
                 its pages is in this answer, which is a fact ABOUT the website
                 rather than a gap in the measurement, and keeps every tool
             Its `why` is the sentence for each, so this screen states the case
             instead of guessing at one. */
          <div className="bi-empty-state">
            <span className="bi-empty-state__icon"><I name="list" size={30} /></span>
            <div className="bi-empty-state__title">
              {readNothing
                ? "The crawl found no pages to read"
                : (inv.state && inv.state.read_and_empty)
                  ? (atAll ? "This website was read and holds no pages" : `${nameOf(section)} was read and is empty`)
                  : "Nothing has been crawled yet"}
            </div>
            <p className="bi-empty-state__desc">
              {/* "Nothing is wrong" is only honest before a crawl has been
                  tried. Once one has run and come back with nothing, saying it
                  would contradict the report sitting right above this. */}
              {readNothing
                ? "The last refresh reached the website but came back with no pages. The usual cause is a sitemap that is missing, unreachable, or served as something other than XML. Check that it loads in a browser, then refresh again."
                : (inv.state && inv.state.why)
                  ? inv.state.why + ((inv.state.read_and_empty && atAll) ? " Every tool on this screen still applies." : "")
                  : "MarketLens has not read this website's pages yet, so there is nothing to show. Press Refresh: it reads the sitemap, then each page it lists, and the inventory appears here. Nothing is wrong."}
            </p>
            <div className="bi-empty-state__actions">
              {crawlTargets.length > 0 && (
                <button type="button" className="bi-btn bi-btn--primary" disabled={!!refreshing} onClick={refresh}>
                  {refreshing
                    ? <><span className="ml-spinner ml-spinner--sm ml-spinner--on-primary" style={{ width: 14, height: 14 }} />{`Crawling ${refreshing.name} (${refreshing.done + 1} of ${refreshing.total})…`}</>
                    : <><I name="refresh" size={16} />{crawlTargets.length > 1 ? "Refresh every crawl" : "Refresh"}</>}
                </button>
              )}
              {/* Still offered when this section is empty, because a section
                  with no served pages keeps its tools: the website around it is
                  what the other two screens read. */}
              <button type="button" className="bi-btn" onClick={() => goTo("site-audit")}><I name="shield-check" size={16} />Site Audit</button>
              <button type="button" className="bi-btn" onClick={() => goTo("performance")}><I name="trending-up" size={16} />Performance</button>
            </div>
          </div>
        ) : (
          <>
            <div className="crawl-toolbar">
              <div className="hds-input-group crawl-toolbar__search">
                <span className="hds-input-group__icon"><I name="search" size={16} /></span>
                <input className="hds-input" placeholder="Search by path, title or section" aria-label="Search pages" value={q} onChange={e => setQ(e.target.value)} />
              </div>
              <div className="kw-seg kw-seg--lg" role="tablist" aria-label="Which pages to list">
                {[["all", "All pages"], ["flagged", "With signals"]].map(([id, label]) => (
                  <button key={id} className={view === id ? "on" : ""} aria-pressed={view === id} onClick={() => setView(id)}>{label}</button>
                ))}
              </div>
              <span className="crawl-toolbar__count">
                {fmt(rows.length)} of {fmt(pages.length)} page{pages.length === 1 ? "" : "s"}
              </span>
            </div>

            {rows.length === 0 ? (
              <div className="bi-subempty">
                {view === "flagged" ? "No page in this inventory has a signal on it." : "No page matches that search."}
              </div>
            ) : (
              <div className="ds-lines">
                {/* The piece a row is LISTED under is part of its identity now:
                    the same URL could be reached through two pieces of one
                    website, and a key of the URL alone would collide. */}
                {rows.map(p => <PageLine key={(p.listed_under == null ? "none" : p.listed_under) + ":" + (p.url || p.path)} page={p} />)}
              </div>
            )}

            <div className="seo-stamp">
              <I name="clock" size={13} />
              <span>Last crawled {inv.crawledAt ? stamp(inv.crawledAt) : "never"}.</span>
              <span>This list is a measurement of what the website serves. Nothing on this screen writes to it.</span>
            </div>
          </>
        )}
      </div>
    );
  }

  window.MlSeoPages = { PageInventory, CrawlOnlyNotice, isCrawlSite, isCrawlOnlyError };
})();
